"use client";

import Image from "next/image";
import { CircleCheck, CircleX, User, Workflow } from "lucide-react";
import { useEffect, useRef, useState, type CSSProperties } from "react";

import { CcSanjayaEyebrow } from "@/components/ui/CcSanjayaEyebrow";
import { devSectionAttr } from "@/lib/dev/section-attr";
import { useCcPrefersReducedMotion } from "@/lib/use-cc-prefers-reduced-motion";

import type { SiSubpageCopy } from "./si-page-copy";

type CcSiWhatIsProps = {
  copy: SiSubpageCopy["whatIs"];
};

type Mode = "before" | "after";

// Corners of the diagram (in %), one per system.
const CORNERS: [number, number][] = [
  [16, 20],
  [84, 20],
  [16, 80],
  [84, 80],
];

/**
 * "What is system integration": a plain definition, then a diagram that
 * switches between "without integration" (a person copying data between
 * every pair of systems) and "with integration" (every system connected to
 * one hub). It flips to "with" once when it scrolls into view.
 */
export function CcSiWhatIs({ copy }: CcSiWhatIsProps) {
  const reduced = useCcPrefersReducedMotion();
  const [picked, setPicked] = useState<Mode | null>(null);
  const [flipped, setFlipped] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const root = rootRef.current;
    if (!root || reduced) return;
    let timer = 0;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          timer = window.setTimeout(() => setFlipped(true), 1400);
          observer.disconnect();
        }
      },
      { threshold: 0.5 },
    );
    observer.observe(root);
    return () => {
      observer.disconnect();
      window.clearTimeout(timer);
    };
  }, [reduced]);

  const mode: Mode = picked ?? (reduced || flipped ? "after" : "before");
  const side = mode === "after" ? copy.after : copy.before;
  // Every pair of systems for the tangled "before" state.
  const pairs = CORNERS.flatMap((a, i) => CORNERS.slice(i + 1).map((b) => [a, b] as const));

  return (
    <section
      className="cc-section cc-si-what"
      id="integration-what"
      aria-labelledby="cc-si-what-title"
      data-cc-nav="light"
      {...devSectionAttr("integration-what")}
    >
      <div className="cc-si-what__container">
        <div className="cc-si-what__copy">
          <CcSanjayaEyebrow theme="on-subtle">{copy.eyebrow}</CcSanjayaEyebrow>
          <h2 id="cc-si-what-title" className="cc-si-what__title">
            {copy.title}
          </h2>
          <p className="cc-si-what__text">{copy.text}</p>
        </div>

        <div ref={rootRef} className="cc-si-what__visual" data-mode={mode}>
          <div className="cc-si-what__toggle" role="group" aria-label={copy.toggle.label}>
            {(["before", "after"] as const).map((option) => (
              <button key={option} type="button" aria-pressed={mode === option} onClick={() => setPicked(option)}>
                {copy.toggle[option]}
              </button>
            ))}
          </div>

          <div className="cc-si-diagram" aria-hidden="true">
            <svg viewBox="0 0 100 100" preserveAspectRatio="none">
              <g className="cc-si-diagram__tangle">
                {pairs.map(([a, b], index) => (
                  <line key={index} x1={a[0]} y1={a[1]} x2={b[0]} y2={b[1]} />
                ))}
              </g>
              <g className="cc-si-diagram__hub-lines">
                {CORNERS.map(([x, y], index) => (
                  <line key={index} x1="50" y1="50" x2={x} y2={y} style={{ "--cc-si-i": index } as CSSProperties} />
                ))}
              </g>
            </svg>
            {copy.systems.map((system, index) => (
              <span
                key={system.id}
                className="cc-si-diagram__system"
                style={{ left: `${CORNERS[index][0]}%`, top: `${CORNERS[index][1]}%` } as CSSProperties}
              >
                <Image src={system.logo} alt="" width={22} height={22} unoptimized />
                <b>{system.name}</b>
              </span>
            ))}
            <span className="cc-si-diagram__center cc-si-diagram__center--person">
              <User size={18} strokeWidth={2.2} />
              <small>{copy.before.person}</small>
            </span>
            <span className="cc-si-diagram__center cc-si-diagram__center--hub">
              <Workflow size={20} strokeWidth={2.2} />
              <small>{copy.after.hub}</small>
            </span>
          </div>

          <div className="cc-si-what__result">
            <strong>{side.title}</strong>
            <ul>
              {side.points.map((point) => (
                <li key={point}>
                  {mode === "after" ? (
                    <CircleCheck size={15} strokeWidth={2.4} aria-hidden="true" />
                  ) : (
                    <CircleX size={15} strokeWidth={2.4} aria-hidden="true" />
                  )}
                  {point}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}
